/**
 * StringsTab - Translation management organized by app screens
 * Left column: English (default), Right column: switchable language
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  FiChevronDown, FiChevronRight, FiSearch, FiSave, FiCheck,
  FiAlertCircle, FiPlus, FiCopy, FiHome, FiMenu, FiSettings,
  FiAward, FiMap, FiImage, FiCalendar, FiActivity, FiCreditCard,
  FiUser, FiLayout, FiLock, FiPlayCircle, FiTarget, FiBarChart2,
  FiMoreHorizontal
} from 'react-icons/fi';

// Icon mapping for screens
const SCREEN_ICONS = {
  'lock': FiLock,
  'play-circle': FiPlayCircle,
  'home': FiHome,
  'menu': FiMenu,
  'map': FiMap,
  'target': FiTarget,
  'award': FiAward,
  'image': FiImage,
  'credit-card': FiCreditCard,
  'activity': FiActivity,
  'bar-chart-2': FiBarChart2,
  'calendar': FiCalendar,
  'settings': FiSettings,
  'user': FiUser,
  'layout': FiLayout,
  'more-horizontal': FiMoreHorizontal
};

const StringsTab = ({ deploymentKey = 'discover-flaeming' }) => {
  const [screens, setScreens] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedScreens, setExpandedScreens] = useState(['home', 'menu']);
  const [pendingChanges, setPendingChanges] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);
  const [showOnlyMissing, setShowOnlyMissing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newString, setNewString] = useState({ key: '', screen: 'common' });
  const [selectedLang, setSelectedLang] = useState('de'); // Right column language

  // Fetch translation screens data
  const fetchScreens = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/v1/translations/${deploymentKey}/screens`);
      setScreens(response.data.screens || []);
      setLanguages(response.data.languages || []);

      // Set default selected language (first non-English enabled language)
      const nonEnLangs = (response.data.languages || []).filter(l => l.enabled && l.code !== 'en');
      if (nonEnLangs.length > 0 && !nonEnLangs.find(l => l.code === selectedLang)) {
        setSelectedLang(nonEnLangs[0].code);
      }
    } catch (error) {
      console.error('Failed to fetch translations:', error);
      setSaveStatus({ type: 'error', message: 'Failed to load translations' });
    } finally {
      setLoading(false);
    }
  }, [deploymentKey, selectedLang]);

  useEffect(() => {
    fetchScreens();
  }, [fetchScreens]);

  // Get non-English enabled languages for tabs
  const otherLanguages = languages.filter(l => l.enabled && l.code !== 'en');

  // Toggle screen expansion
  const toggleScreen = (screenId) => {
    setExpandedScreens(prev =>
      prev.includes(screenId)
        ? prev.filter(id => id !== screenId)
        : [...prev, screenId]
    );
  };

  // Get display value (pending change or original)
  // Convert actual newlines to literal \n for display in input fields
  const getDisplayValue = (key, lang, originalValue) => {
    const changeKey = `${key}:${lang}`;
    if (pendingChanges.hasOwnProperty(changeKey)) {
      return pendingChanges[changeKey];
    }
    // Convert actual newlines to literal \n for display
    return originalValue ? originalValue.replace(/\n/g, '\\n') : '';
  };

  // Handle value change
  const handleValueChange = (key, lang, value) => {
    const changeKey = `${key}:${lang}`;
    setPendingChanges(prev => ({
      ...prev,
      [changeKey]: value
    }));
  };

  // Save all pending changes
  const saveAllChanges = async () => {
    setSaving(true);
    setSaveStatus(null);

    const changes = Object.entries(pendingChanges);
    const translationsToSave = changes.map(([changeKey, value]) => {
      const [key, lang] = changeKey.split(':');
      // Convert literal \n to actual newlines
      const processedValue = value ? value.replace(/\\n/g, '\n') : value;
      return { key, language: lang, value: processedValue };
    });

    try {
      const response = await api.put(
        `/v1/translations/${deploymentKey}/bulk`,
        { translations: translationsToSave }
      );

      setPendingChanges({});
      await fetchScreens();

      if (response.data.errorCount === 0) {
        setSaveStatus({ type: 'success', message: `Saved ${response.data.successCount} translation(s)` });
      } else {
        setSaveStatus({ type: 'error', message: `${response.data.errorCount} failed` });
      }
    } catch (error) {
      console.error('Failed to save translations:', error);
      setSaveStatus({ type: 'error', message: 'Failed to save' });
    }

    setSaving(false);
    setTimeout(() => setSaveStatus(null), 3000);
  };

  // Add new string
  const handleAddString = async () => {
    if (!newString.key) return;

    setSaving(true);

    try {
      await api.put(
        `/v1/translations/${deploymentKey}/${newString.key}`,
        { language: 'en', value: '' }
      );

      await fetchScreens();
      setShowAddModal(false);
      setNewString({ key: '', screen: 'common' });
      setSaveStatus({ type: 'success', message: 'String added' });
    } catch (error) {
      console.error('Failed to add string:', error);
      setSaveStatus({ type: 'error', message: 'Failed to add string' });
    } finally {
      setSaving(false);
    }
  };

  // Copy key to clipboard
  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    setSaveStatus({ type: 'success', message: `Copied: ${key}` });
    setTimeout(() => setSaveStatus(null), 2000);
  };

  // Filter strings by search
  const filterStrings = (strings) => {
    if (!searchTerm) return strings;
    const term = searchTerm.toLowerCase();
    return strings.filter(s => {
      if (s.key.toLowerCase().includes(term)) return true;
      return Object.values(s.translations).some(v =>
        (v || '').toLowerCase().includes(term)
      );
    });
  };

  // Filter to show only missing
  const filterMissing = (strings) => {
    if (!showOnlyMissing) return strings;
    return strings.filter(s => !s.translations[selectedLang]);
  };

  // Render a single string row
  const renderStringRow = (stringData, index) => {
    const { key, translations } = stringData;
    const enValue = getDisplayValue(key, 'en', translations['en']);
    const targetValue = getDisplayValue(key, selectedLang, translations[selectedLang]);
    const hasEnChange = pendingChanges.hasOwnProperty(`${key}:en`);
    const hasTargetChange = pendingChanges.hasOwnProperty(`${key}:${selectedLang}`);
    const isMissing = !translations[selectedLang];

    return (
      <tr key={key} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
        {/* Key column */}
        <td className="px-2 py-1.5 text-xs font-mono text-gray-400 w-44 min-w-[176px]">
          <div className="flex items-center gap-1">
            <button
              onClick={() => copyKey(key)}
              className="p-0.5 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600 flex-shrink-0"
              title="Copy key"
            >
              <FiCopy className="w-3 h-3" />
            </button>
            <span className="truncate" title={key}>{key}</span>
          </div>
        </td>

        {/* English column */}
        <td className={`px-2 py-1.5 w-1/2 ${hasEnChange ? 'bg-blue-50' : ''}`}>
          <input
            type="text"
            value={enValue}
            onChange={(e) => handleValueChange(key, 'en', e.target.value)}
            className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              hasEnChange ? 'border-blue-400' : 'border-gray-200'
            }`}
          />
        </td>

        {/* Target language column */}
        <td className={`px-2 py-1.5 w-1/2 ${isMissing ? 'bg-yellow-50' : ''} ${hasTargetChange ? 'bg-blue-50' : ''}`}>
          <input
            type="text"
            value={targetValue}
            onChange={(e) => handleValueChange(key, selectedLang, e.target.value)}
            placeholder={isMissing ? 'Missing translation...' : ''}
            className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              isMissing ? 'border-yellow-300 placeholder-yellow-400' : 'border-gray-200'
            } ${hasTargetChange ? 'border-blue-400' : ''}`}
          />
        </td>
      </tr>
    );
  };

  // Render a screen section
  const renderScreen = (screen) => {
    const filteredStrings = filterMissing(filterStrings(screen.strings || []));
    if (filteredStrings.length === 0 && searchTerm) return null;

    const isExpanded = expandedScreens.includes(screen.id);
    const Icon = SCREEN_ICONS[screen.icon] || FiLayout;

    // Count missing for selected language
    const missingForLang = (screen.strings || []).filter(s => !s.translations[selectedLang]).length;

    return (
      <div key={screen.id} className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Screen Header */}
        <button
          onClick={() => toggleScreen(screen.id)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <FiChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <FiChevronRight className="w-4 h-4 text-gray-500" />
            )}
            <Icon className="w-4 h-4 text-primary-600" />
            <span className="font-medium text-gray-900 text-sm">{screen.name}</span>
            <span className="text-xs text-gray-400">({screen.keyCount})</span>
            {missingForLang > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                {missingForLang} missing
              </span>
            )}
          </div>
          <span className="text-xs text-gray-400 hidden sm:block">{screen.description}</span>
        </button>

        {/* Screen Content */}
        {isExpanded && (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-gray-100 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 w-44">Key</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 w-1/2">
                    English <span className="text-blue-500 normal-case">(default)</span>
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-primary-700 bg-primary-50 w-1/2">
                    {languages.find(l => l.code === selectedLang)?.nativeName || selectedLang}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredStrings.map((s, i) => renderStringRow(s, i))}
                {filteredStrings.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-gray-400 text-sm">
                      {searchTerm ? 'No matching strings' : 'No strings in this screen'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-gray-600">Loading translations...</span>
      </div>
    );
  }

  const pendingCount = Object.keys(pendingChanges).length;
  const totalMissing = screens.reduce((acc, s) =>
    acc + (s.strings || []).filter(str => !str.translations[selectedLang]).length, 0
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">App Translations</h2>
          <p className="text-sm text-gray-500">
            {screens.reduce((acc, s) => acc + s.keyCount, 0)} strings across {screens.length} screens
            {totalMissing > 0 && <span className="text-yellow-600 ml-1">({totalMissing} missing in {languages.find(l => l.code === selectedLang)?.name})</span>}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {saveStatus && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
              saveStatus.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {saveStatus.type === 'success' ? <FiCheck className="w-3 h-3" /> : <FiAlertCircle className="w-3 h-3" />}
              {saveStatus.message}
            </div>
          )}

          {pendingCount > 0 && (
            <button
              onClick={saveAllChanges}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              <FiSave className="w-4 h-4" />
              {saving ? 'Saving...' : `Save (${pendingCount})`}
            </button>
          )}

          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
          >
            <FiPlus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      {/* Language Tabs */}
      {otherLanguages.length > 0 && (
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg w-fit">
          <span className="text-xs text-gray-500 px-2">Translate to:</span>
          {otherLanguages.map(lang => (
            <button
              key={lang.code}
              onClick={() => setSelectedLang(lang.code)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                lang.code === selectedLang
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {lang.nativeName || lang.name}
            </button>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
        {/* Search */}
        <div className="flex-1 relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search keys or values..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Show only missing */}
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={showOnlyMissing}
            onChange={(e) => setShowOnlyMissing(e.target.checked)}
            className="rounded border-gray-300 text-primary-600"
          />
          Missing only
        </label>

        {/* Expand/Collapse */}
        <button
          onClick={() => setExpandedScreens(
            expandedScreens.length === screens.length ? [] : screens.map(s => s.id)
          )}
          className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded"
        >
          {expandedScreens.length === screens.length ? 'Collapse' : 'Expand'} All
        </button>
      </div>

      {/* Screens */}
      <div className="space-y-3">
        {screens.map(screen => renderScreen(screen))}
      </div>

      {/* Add String Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-4 py-3 border-b">
              <h3 className="font-medium">Add New String</h3>
            </div>
            <div className="px-4 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">String Key</label>
                <input
                  type="text"
                  value={newString.key}
                  onChange={(e) => setNewString(prev => ({
                    ...prev,
                    key: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
                  }))}
                  placeholder="e.g., settings_dark_mode"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use prefix to assign to screen: dashboard_, menu_, settings_, etc.
                </p>
              </div>

              <div className="p-2 bg-gray-50 rounded text-sm">
                <span className="text-gray-500">Will be added to: </span>
                <span className="font-medium">
                  {screens.find(s => s.keys?.some(k => newString.key.startsWith(k.split('_')[0] + '_')))?.name || 'Other'}
                </span>
              </div>
            </div>
            <div className="px-4 py-3 border-t flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewString({ key: '', screen: 'common' });
                }}
                className="px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleAddString}
                disabled={!newString.key || saving}
                className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                Add String
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StringsTab;
